import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import EmailTemplate from "@mapmycustomers/shared/types/email/EmailTemplate";
import { EntitiesSupportedByEmailFeature, Resource } from "@mapmycustomers/shared/types/entity";
import parseEmailDynamicVariables from "@mapmycustomers/shared/util/email/parseEmailDynamicVariables";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { Alert, Divider } from "@mapmycustomers/ui";

import EmailSubjectView from "@app/component/email/creation/EmailSubjectView";
import EmailBodyView from "@app/component/email/EmailBodyView";
import { getEmailTemplateInfo } from "@app/store/email";
import { updateEmailTemplate } from "@app/store/email/actions";
import { getCurrentUserId } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import getEmailDynamicVariablesInfo from "@app/util/fieldModel/util/getEmailDynamicVariablesInfo";

import styles from "./EmailTemplateView.module.scss";
import messages from "./messages";

interface EmailTemplateViewProps {
  readOnly?: boolean;
  recipients?: EntitiesSupportedByEmailFeature[];
  showLabels?: boolean;
  templateId: EmailTemplate["id"];
}

const EmailTemplateView = ({
  currentUserId,
  onTemplateUpdate,
  readOnly,
  recipients,
  showLabels,
  templateInfo: { resources, template, valid },
}: EmailTemplateViewProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();

  const [resourcesRemovedAlertVisible, showResourcesRemovedAlert, hideResourcedRemovedAlert] =
    useBoolean();

  const isCurrentUserOwner = currentUserId === template.userId;
  const removeInvalidResourcesButtonDisabled = !isCurrentUserOwner && !valid;

  const [subjectDynamicVariables] = useMemo(
    () =>
      getEmailDynamicVariablesInfo({
        entities: recipients,
        entityType: template.entityType,
        includeOnlyParsedDynamicVariables: true,
        intl,
        parsedDynamicVariables: parseEmailDynamicVariables(template.subject),
      }),
    [intl, template, recipients]
  );

  useEffect(() => {
    hideResourcedRemovedAlert();
  }, [template.id, hideResourcedRemovedAlert]);

  const handleInvalidResourceLinksRemoved = useDynamicCallback(
    (value: string, resources: Resource[]) => {
      onTemplateUpdate({
        callback: showResourcesRemovedAlert,
        resources,
        template: {
          ...template,
          body: value,
        },
      });
    }
  );

  return (
    <div className={styles.container}>
      {showLabels && (
        <div className={styles.label}>{intl.formatMessage(messages.sectionSubject)}</div>
      )}
      <EmailSubjectView dynamicVariables={subjectDynamicVariables} subject={template.subject} />
      <Divider className={styles.divider} />
      {showLabels && <div className={styles.label}>{intl.formatMessage(messages.sectionBody)}</div>}
      <EmailBodyView
        attachments={template.attachments ?? []}
        body={template.body}
        entityType={template.entityType}
        onInvalidResourceLinksRemoved={readOnly ? undefined : handleInvalidResourceLinksRemoved}
        removeInvalidResourcesButtonProps={
          readOnly
            ? undefined
            : {
                disabled: removeInvalidResourcesButtonDisabled,
                tooltip: removeInvalidResourcesButtonDisabled
                  ? intl.formatMessage(messages.removeInvalidResourcesDisabledTooltip)
                  : undefined,
              }
        }
        removeResourcesAlertSuffix={
          readOnly ? "" : intl.formatMessage(messages.removeInvalidResourcesAlertSuffix)
        }
        resources={resources}
        resourcesMetadata={readOnly ? undefined : template.metadata?.resources}
      />
      {!readOnly && resourcesRemovedAlertVisible && (
        <Alert
          className={styles.alert}
          closable
          message={intl.formatMessage(messages.resourcesRemoved)}
          onClose={hideResourcedRemovedAlert}
          showIcon
          type="success"
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState, { templateId }: EmailTemplateViewProps) => ({
  currentUserId: getCurrentUserId(state)!,
  templateInfo: getEmailTemplateInfo(state, templateId)!,
});

const mapDispatchToProps = {
  onTemplateUpdate: updateEmailTemplate.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(EmailTemplateView);
